/**
 * Utilitaires pour la gestion des entités (clients/fournisseurs)
 *
 * Ces fonctions garantissent l'intégrité des données en éliminant les doublons
 * qui peuvent survenir suite à des jointures SQL multiples dans la base de données.
 */

/**
 * Interface représentant une entité (client ou fournisseur)
 */
export interface Entity {
  id: number;
  [key: string]: any;
}

export interface SearchableCommercialEntity {
  id?: unknown;
  user_id?: unknown;
  name?: unknown;
  company_name?: unknown;
  Nom_Raison_Sociale?: unknown;
  email?: unknown;
  phone?: unknown;
  address?: unknown;
  rccm?: unknown;
  ifu?: unknown;
}

export const COMMERCIAL_ENTITY_RCCM_MAX_LENGTH = 191;
export const COMMERCIAL_ENTITY_RCCM_LENGTH_MESSAGE =
  `Le numéro RCCM ne doit pas dépasser ${COMMERCIAL_ENTITY_RCCM_MAX_LENGTH} caractères.`;

export function getCommercialEntityRccmError(value: unknown): string | null {
  return Array.from(String(value ?? "")).length > COMMERCIAL_ENTITY_RCCM_MAX_LENGTH
    ? COMMERCIAL_ENTITY_RCCM_LENGTH_MESSAGE
    : null;
}

const normalizeSearchText = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * Retourne le nom à afficher pour un client ou un fournisseur, quel que soit
 * le format historique reçu de l'API.
 */
export function getCommercialEntityDisplayName(
  entity: SearchableCommercialEntity | null | undefined,
  fallback = "Nom inconnu"
): string {
  const candidate =
    entity?.name ?? entity?.company_name ?? entity?.Nom_Raison_Sociale;
  const normalizedCandidate = String(candidate ?? "").trim();

  return normalizedCandidate || fallback;
}

/**
 * Construit le contenu de recherche invisible d'une option de tiers.
 * Le libellé reste compact, mais la saisie peut aussi cibler l'e-mail, le
 * téléphone et les principaux identifiants administratifs.
 */
export function buildCommercialEntitySearchText(
  entity: SearchableCommercialEntity | null | undefined
): string {
  if (!entity) return "";

  return [
    getCommercialEntityDisplayName(entity, ""),
    entity.email,
    entity.phone,
    entity.address,
    entity.rccm,
    entity.ifu,
    entity.id,
    entity.user_id,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(" ");
}

/**
 * Filtre au fil de la saisie, sans tenir compte de la casse ni des accents.
 * Chaque mot saisi peut correspondre à une partie différente de la fiche.
 */
export function matchesCommercialEntitySearch(
  searchableValue: unknown,
  query: unknown
): boolean {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  const normalizedValue = normalizeSearchText(searchableValue);
  return normalizedQuery
    .split(/\s+/)
    .every((token) => normalizedValue.includes(token));
}

/**
 * Dédoublonne un tableau d'entités basé sur leur ID
 *
 * Problème résolu :
 * - Les requêtes SQL avec LEFT JOIN sur user_details peuvent retourner plusieurs lignes
 *   pour un même utilisateur si celui-ci a plusieurs entrées dans user_details
 * - Cela crée des doublons visuels dans les listes déroulantes (Select, Autocomplete)
 *
 * Solution :
 * - Utilise une Map avec l'ID comme clé pour garantir l'unicité
 * - Garde la première occurrence de chaque entité (comportement par défaut)
 * - Préserve toutes les propriétés de l'entité
 *
 * @param entities - Tableau d'entités pouvant contenir des doublons
 * @returns Tableau d'entités uniques (un seul élément par ID)
 *
 * @example
 * ```typescript
 * const clients = await fetch('/api/users/customers').then(r => r.json());
 * // clients peut contenir: [{id: 1, name: "Jean"}, {id: 1, name: "Jean"}, {id: 2, name: "Marie"}]
 *
 * const uniqueClients = deduplicateEntities(clients);
 * // uniqueClients = [{id: 1, name: "Jean"}, {id: 2, name: "Marie"}]
 * ```
 */
export function deduplicateEntities<T extends Entity>(entities: T[]): T[] {
  if (!Array.isArray(entities) || entities.length === 0) {
    return entities;
  }

  // Utilise une Map pour garantir l'unicité par ID
  // La Map préserve l'ordre d'insertion et ne garde que la première occurrence de chaque ID
  const uniqueMap = new Map<number, T>();

  for (const entity of entities) {
    // Vérifie que l'entité a un ID valide
    if (entity && typeof entity.id === 'number' && !uniqueMap.has(entity.id)) {
      uniqueMap.set(entity.id, entity);
    }
  }

  // Convertit la Map en tableau
  return Array.from(uniqueMap.values());
}

/**
 * Compte le nombre de doublons dans un tableau d'entités
 * Utile pour le debugging et les logs
 *
 * @param entities - Tableau d'entités à analyser
 * @returns Objet contenant le total, les uniques, et le nombre de doublons
 *
 * @example
 * ```typescript
 * const stats = countDuplicates(clients);
 * console.log(`${stats.total} entrées, ${stats.unique} uniques, ${stats.duplicates} doublons`);
 * ```
 */
export function countDuplicates<T extends Entity>(entities: T[]): {
  total: number;
  unique: number;
  duplicates: number;
} {
  if (!Array.isArray(entities)) {
    return { total: 0, unique: 0, duplicates: 0 };
  }

  const total = entities.length;
  const uniqueIds = new Set(entities.map(e => e.id));
  const unique = uniqueIds.size;
  const duplicates = total - unique;

  return { total, unique, duplicates };
}

/**
 * Trouve les entités dupliquées et retourne des informations détaillées
 * Utile pour le debugging avancé
 *
 * @param entities - Tableau d'entités à analyser
 * @returns Tableau d'objets décrivant chaque doublon détecté
 */
export function findDuplicates<T extends Entity>(entities: T[]): Array<{
  id: number;
  count: number;
  entities: T[];
}> {
  if (!Array.isArray(entities) || entities.length === 0) {
    return [];
  }

  const duplicatesMap = new Map<number, T[]>();

  // Groupe les entités par ID
  for (const entity of entities) {
    if (entity && typeof entity.id === 'number') {
      if (!duplicatesMap.has(entity.id)) {
        duplicatesMap.set(entity.id, []);
      }
      duplicatesMap.get(entity.id)!.push(entity);
    }
  }

  // Filtre pour ne garder que les IDs avec plusieurs occurrences
  const duplicates: Array<{ id: number; count: number; entities: T[] }> = [];

  duplicatesMap.forEach((entities, id) => {
    if (entities.length > 1) {
      duplicates.push({
        id,
        count: entities.length,
        entities,
      });
    }
  });

  return duplicates;
}
